import type { WorldRoomProjection } from "@cleo/contracts";
import { AnimatedList } from "@neucleos/motion-primitives";
import { ArchiveRestore, ChevronRight, EllipsisVertical } from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "./ui/popover";

export interface ArchivedRoomChoice {
  readonly room: WorldRoomProjection;
  readonly sessionCount: number;
}

/** World-held rooms; this picker owns only the temporary selection and search. */
export function RoomArchiveMenu({
  rooms,
  onRestore,
  renderArrangement,
}: {
  readonly rooms: ReadonlyArray<ArchivedRoomChoice>;
  /** The Arrange controls, given the menu's close (Tidy up opens a dialog in front of it). */
  readonly renderArrangement?: (closeMenu: () => void) => ReactNode;
  readonly onRestore: (room: WorldRoomProjection) => Promise<string | null>;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const restoredRoomId = useRef<string | null>(null);
  const focusRestoredRoom = () => {
    if (restoredRoomId.current === null) return true;
    const header = [...document.querySelectorAll<HTMLElement>("[data-room-rail-room]")].find(
      (node) => node.dataset.roomRailRoom === restoredRoomId.current,
    );
    if (!header) return true;
    header.scrollIntoView?.({ block: "nearest", behavior: "auto" });
    return header.matches('[role="button"]') ? header : header.querySelector("button");
  };
  const filtered = useMemo(
    () =>
      rooms.filter(({ room }) =>
        room.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
      ),
    [rooms, search],
  );
  const selected = filtered.find(({ room }) => room.roomId === selectedId)?.room;
  const reset = () => {
    setSearch("");
    setSelectedId(null);
    setError(null);
  };
  const restore = async () => {
    if (!selected || restoring) return;
    setRestoring(true);
    setError(null);
    try {
      const reason = await onRestore(selected);
      if (reason !== null) setError(reason);
      else {
        restoredRoomId.current = selected.roomId;
        setPickerOpen(false);
        setMenuOpen(false);
        reset();
      }
    } finally {
      setRestoring(false);
    }
  };
  return (
    <Popover
      open={menuOpen}
      onOpenChange={(open) => {
        setMenuOpen(open);
        if (open) restoredRoomId.current = null;
        if (!open) {
          setPickerOpen(false);
          reset();
        }
      }}
    >
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label="Rooms menu"
            title="Rooms menu"
            data-room-rail-actions=""
            className="flex size-6 shrink-0 items-center justify-center rounded hover:bg-accent focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
          />
        }
      >
        <EllipsisVertical aria-hidden className="size-3" />
      </PopoverTrigger>
      <PopoverPopup
        align="start"
        className={renderArrangement ? "w-64" : "w-44"}
        viewportClassName="p-1"
        finalFocus={focusRestoredRoom}
      >
        {renderArrangement?.(() => setMenuOpen(false))}
        <Popover
          open={pickerOpen}
          onOpenChange={(open) => {
            setPickerOpen(open);
            if (!open) reset();
          }}
        >
          <PopoverTrigger
            render={
              <button
                type="button"
                data-room-rail-restore-menu=""
                className="flex min-h-8 w-full items-center gap-2 rounded px-2 text-left text-xs hover:bg-accent focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
              />
            }
          >
            <ArchiveRestore aria-hidden className="size-3.5" />
            <span className="flex-1">Restore room…</span>
            <ChevronRight aria-hidden className="size-3" />
          </PopoverTrigger>
          <PopoverPopup
            side="right"
            align="start"
            sideOffset={8}
            className="w-80 max-w-[calc(100vw-24px)]"
            data-room-restore-picker=""
            finalFocus={() => restoredRoomId.current === null}
          >
            <PopoverTitle className="text-sm">Restore room</PopoverTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Choose an archived room to return to the rail.
            </p>
            <Input
              aria-label="Search archived rooms"
              placeholder="Search rooms…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setSelectedId(null);
              }}
              className="mt-3"
            />
            <div className="mt-3 flex h-60 min-h-0 flex-col" data-room-restore-list="">
              <AnimatedList
                items={filtered}
                keyOf={({ room }) => room.roomId}
                settle={false}
                displayScrollbar
                enableArrowNavigation={false}
                ariaLabel="Archived rooms"
                cleo="restore-rooms"
                emptyState={
                  <p className="px-2 py-4 text-xs text-muted-foreground">
                    {rooms.length === 0 ? "No archived rooms." : "No matching rooms."}
                  </p>
                }
                renderItem={({ room, sessionCount }) => (
                  <button
                    type="button"
                    aria-pressed={room.roomId === selectedId}
                    disabled={restoring}
                    data-room-restore-choice={room.roomId}
                    onClick={() => {
                      setSelectedId(room.roomId);
                      setError(null);
                    }}
                    className="flex w-full min-w-0 flex-col gap-1 rounded-lg px-3 py-2 text-left hover:bg-accent aria-pressed:bg-accent focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
                  >
                    <span className="w-full truncate text-xs font-medium">{room.label}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {sessionCount} {sessionCount === 1 ? "session" : "sessions"}
                      {room.kind === "workroom" ? " · Workroom" : ""}
                    </span>
                  </button>
                )}
              />
            </div>
            {error ? (
              <p role="alert" className="mt-2 text-xs text-destructive-foreground">
                {error}
              </p>
            ) : null}
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setPickerOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!selected || restoring}
                onClick={() => void restore()}
                data-room-restore-submit=""
              >
                {restoring ? "Restoring…" : "Restore"}
              </Button>
            </div>
          </PopoverPopup>
        </Popover>
      </PopoverPopup>
    </Popover>
  );
}
